import { useCallback, useEffect, useRef } from 'react';
import { callBackend } from '../../../utils/api';
import {
    FACTOR_DATA_FRESHNESS_POLL_MS,
    getFactorContextKey,
    normalizeFactorDataStatusPayload,
} from '../dashboardUtils';

export default function useFactorDataFreshnessPolling({
    userUid,
    startupReady = true,
    selectedFactorModel,
    requestedFactorDataModel,
    pollIntervalMs = FACTOR_DATA_FRESHNESS_POLL_MS,
    refreshSignal = 0,
    acceptedFactorContextKey = '',
    matricesLoading = false,
    coreLoadInFlightRef,
    exclusiveCoreLoadOwnerRef,
    priceRefreshInFlightRef,
    importApplyInFlightRef,
    onStatus,
    onInitialStatusAccepted,
    onPendingFactorContext,
    onPendingFactorContextCleared,
}) {
    const latestAcceptedMatrixScopeVersionRef = useRef('');
    const latestStagedMatrixScopeVersionRef = useRef('');
    const pollInFlightRef = useRef(false);
    const completedRefreshSignalRef = useRef(refreshSignal);
    const pendingRefreshSignalRef = useRef(null);

    useEffect(() => {
        latestAcceptedMatrixScopeVersionRef.current = String(acceptedFactorContextKey || '').trim();
        latestStagedMatrixScopeVersionRef.current = '';
    }, [acceptedFactorContextKey, userUid]);

    const readStatus = useCallback(async () => {
        if (!userUid || !selectedFactorModel) {
            latestAcceptedMatrixScopeVersionRef.current = '';
            latestStagedMatrixScopeVersionRef.current = '';
            onStatus?.(null);
            return null;
        }
        const payload = await callBackend('get_factor_data_status', 'POST', {
            risk_source: selectedFactorModel,
            factor_data_model: requestedFactorDataModel,
        });
        return normalizeFactorDataStatusPayload(payload);
    }, [
        onStatus,
        requestedFactorDataModel,
        selectedFactorModel,
        userUid,
    ]);

    const handleStatus = useCallback(async (status) => {
        onStatus?.(status);
        // A status request can start before a terminal watchlist publication
        // claims the core loader. Do not let its late response launch a
        // competing matrix refresh; the next poll will reconcile against the
        // context atomically promoted by the publication owner.
        if (exclusiveCoreLoadOwnerRef.current) {
            return;
        }
        const nextMatrixScopeVersionKey = getFactorContextKey(status);
        if (!nextMatrixScopeVersionKey) {
            return;
        }

        const acceptedMatrixScopeVersionKey = String(
            acceptedFactorContextKey ||
            latestAcceptedMatrixScopeVersionRef.current ||
            '',
        ).trim();

        if (!acceptedMatrixScopeVersionKey) {
            if (status?.available !== true) {
                latestAcceptedMatrixScopeVersionRef.current = '';
                latestStagedMatrixScopeVersionRef.current = '';
                return;
            }
            latestAcceptedMatrixScopeVersionRef.current = nextMatrixScopeVersionKey;
            latestStagedMatrixScopeVersionRef.current = '';
            onInitialStatusAccepted?.(status);
            return;
        }

        latestAcceptedMatrixScopeVersionRef.current = acceptedMatrixScopeVersionKey;

        if (acceptedMatrixScopeVersionKey === nextMatrixScopeVersionKey) {
            const hadStagedMatrixScopeVersion = Boolean(latestStagedMatrixScopeVersionRef.current);
            latestStagedMatrixScopeVersionRef.current = '';
            if (hadStagedMatrixScopeVersion) {
                onPendingFactorContextCleared?.({
                    status,
                    acceptedMatrixScopeVersionKey,
                    nextMatrixScopeVersionKey,
                });
            }
            return;
        }

        if (latestStagedMatrixScopeVersionRef.current === nextMatrixScopeVersionKey) {
            return;
        }

        latestStagedMatrixScopeVersionRef.current = nextMatrixScopeVersionKey;
        onPendingFactorContext?.({
            status,
            previousMatrixScopeVersionKey: acceptedMatrixScopeVersionKey,
            nextMatrixScopeVersionKey,
        });
    }, [
        acceptedFactorContextKey,
        exclusiveCoreLoadOwnerRef,
        onInitialStatusAccepted,
        onPendingFactorContext,
        onPendingFactorContextCleared,
        onStatus,
    ]);

    const markRefreshSignalAttemptComplete = useCallback((signal) => {
        if (signal == null || pendingRefreshSignalRef.current !== signal) {
            return;
        }
        completedRefreshSignalRef.current = signal;
        pendingRefreshSignalRef.current = null;
    }, []);

    useEffect(() => {
        if (!userUid) {
            latestAcceptedMatrixScopeVersionRef.current = '';
            latestStagedMatrixScopeVersionRef.current = '';
            pendingRefreshSignalRef.current = null;
            onStatus?.(null);
            return undefined;
        }
        if (!startupReady) {
            return undefined;
        }

        let cancelled = false;
        let timeoutId = null;

        const busy = () => (
            matricesLoading ||
            coreLoadInFlightRef?.current ||
            exclusiveCoreLoadOwnerRef.current ||
            priceRefreshInFlightRef?.current ||
            importApplyInFlightRef?.current ||
            pollInFlightRef.current
        );

        const scheduleNext = () => {
            if (cancelled) return;
            timeoutId = window.setTimeout(runPoll, pollIntervalMs);
        };

        const runPoll = async () => {
            if (cancelled) return;
            if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
                scheduleNext();
                return;
            }
            if (busy()) {
                scheduleNext();
                return;
            }

            pollInFlightRef.current = true;
            const pendingRefreshSignalAtPollStart = pendingRefreshSignalRef.current;
            try {
                const status = await readStatus();
                if (!cancelled) {
                    await handleStatus(status);
                }
            } catch (error) {
                console.warn('Failed to poll factor data freshness:', error);
            } finally {
                pollInFlightRef.current = false;
                if (!cancelled) {
                    markRefreshSignalAttemptComplete(pendingRefreshSignalAtPollStart);
                }
                scheduleNext();
            }
        };

        const handleVisibilityChange = () => {
            if (typeof document === 'undefined' || document.visibilityState !== 'visible') {
                return;
            }
            if (timeoutId) {
                window.clearTimeout(timeoutId);
                timeoutId = null;
            }
            void runPoll();
        };

        void runPoll();
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            cancelled = true;
            if (timeoutId) {
                window.clearTimeout(timeoutId);
            }
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [
        coreLoadInFlightRef,
        exclusiveCoreLoadOwnerRef,
        handleStatus,
        importApplyInFlightRef,
        markRefreshSignalAttemptComplete,
        matricesLoading,
        onStatus,
        pollIntervalMs,
        priceRefreshInFlightRef,
        readStatus,
        startupReady,
        userUid,
    ]);

    useEffect(() => {
        if (!userUid) {
            pendingRefreshSignalRef.current = null;
            completedRefreshSignalRef.current = refreshSignal;
            return undefined;
        }

        if (refreshSignal !== completedRefreshSignalRef.current) {
            pendingRefreshSignalRef.current = refreshSignal;
        }

        if (
            pendingRefreshSignalRef.current == null ||
            pendingRefreshSignalRef.current === completedRefreshSignalRef.current
        ) {
            return undefined;
        }

        let cancelled = false;
        let retryTimeoutId = null;

        const externallyBusy = () => (
            matricesLoading ||
            coreLoadInFlightRef?.current ||
            exclusiveCoreLoadOwnerRef.current ||
            priceRefreshInFlightRef?.current ||
            importApplyInFlightRef?.current
        );

        const runImmediateRefresh = async () => {
            if (cancelled) return;
            const signalToAttempt = pendingRefreshSignalRef.current;
            if (signalToAttempt == null || signalToAttempt === completedRefreshSignalRef.current) {
                return;
            }
            if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
                return;
            }
            if (pollInFlightRef.current || externallyBusy()) {
                if (retryTimeoutId) {
                    window.clearTimeout(retryTimeoutId);
                }
                retryTimeoutId = window.setTimeout(runImmediateRefresh, 250);
                return;
            }

            pollInFlightRef.current = true;
            try {
                const status = await readStatus();
                if (!cancelled) {
                    await handleStatus(status);
                }
            } catch (error) {
                console.warn('Failed to refresh factor data freshness:', error);
            } finally {
                pollInFlightRef.current = false;
                markRefreshSignalAttemptComplete(signalToAttempt);
            }
        };

        const handleVisibilityChange = () => {
            if (typeof document === 'undefined' || document.visibilityState !== 'visible') {
                return;
            }
            void runImmediateRefresh();
        };

        void runImmediateRefresh();
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            cancelled = true;
            if (retryTimeoutId) {
                window.clearTimeout(retryTimeoutId);
            }
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [
        coreLoadInFlightRef,
        exclusiveCoreLoadOwnerRef,
        handleStatus,
        importApplyInFlightRef,
        markRefreshSignalAttemptComplete,
        matricesLoading,
        priceRefreshInFlightRef,
        readStatus,
        refreshSignal,
        userUid,
    ]);
}
